import { filterClasses } from "@swivl-mono/helpers";
import { PolicyFieldOption, PolicyFieldType, PolicyReviewGroup } from "@swivl-mono/types";
import { SelectItem } from "@swivl-mono/ui";
import { useState } from "react";
import { FiChevronDown, FiCornerDownRight } from "react-icons/fi";

import { AffectedLocationsDetail, AffectedLocationsField } from "./AffectedLocationsPanel";
import { CompactPolicyValueControl, FreeValueInput, PolicyValueControl } from "./PolicyValueControl";

/**
 * The mutation handlers PoliciesScreen owns (API calls + refreshing the shared `changes` state) —
 * shared verbatim between this list view and PolicyWalkThrough so there is exactly one place that
 * calls the review-toggle/edit endpoints.
 */
export interface PolicyReviewHandlers {
  /**
   * `locationIds` null = the group's org scope (only meaningful for a `scope: "org"` group); a
   * non-null array sets every one of those locations to `excluded` in one call — the multi-select
   * "Done" commit can both include and exclude locations at once, so this always takes a batch,
   * never a single id.
   */
  onToggleExcluded: (group: PolicyReviewGroup, locationIds: number[] | null, excluded: boolean) => void;
  /** `locationId` omitted = a group-wide edit (every non-excluded location in the group takes the
   * new value — only meaningful when they currently all agree on one, i.e. `group.proposedValue`
   * isn't null). Present = a single location's own edit, independent of its siblings — how a
   * "Varies by location" group is corrected, via the per-location breakdown ("After apply"). */
  onEditValue: (group: PolicyReviewGroup, newValue: string, locationId?: number) => void;
  /** Adds a location the scanned document never covered to this group (manufactures its finding). */
  onAddLocations: (group: PolicyReviewGroup, locationIds: number[]) => void;
  /**
   * Writes this group (and, when it has a gated child folded under it, the child's mapKey in the
   * SAME call — approving a child alone would silently no-op, since the parent's proposed value
   * falls out of scope for gatePasses's lookup). `childMapKeys` omitted = no folded children.
   * Resolves once the write (success or failure) has settled — Walk Through awaits it before
   * advancing/closing so it never looks done before the request actually is.
   */
  onApprove: (group: PolicyReviewGroup, childMapKeys?: string[]) => Promise<void>;
  /** Groups with a mutation currently in flight — disables that group's controls only. */
  busyGroupKeys: Set<string>;
  /** One global flag disables every Approve button while any single approve is in flight — see the
   * jsonb race note where this is set (BulkImportPage's `approving` state). */
  approving: boolean;
}

interface PolicyUpdatesListProps {
  groups: PolicyReviewGroup[];
  /** Every org location, grouped by org group — for the "Affects N locations" multi-select. */
  allLocationOptions: SelectItem[];
  handlers: PolicyReviewHandlers;
}

/**
 * Bucket groups into real catalog sections. Groups arrive from the server already sorted by
 * sortOrder/policyGroup/field index, so a section is just a contiguous run of the same
 * `policyGroup` label — shared by the list view and the Walk Through rail so both agree on order.
 */
export function sectionizeGroups(groups: PolicyReviewGroup[]): { label: string; groups: PolicyReviewGroup[] }[] {
  const sections: { label: string; groups: PolicyReviewGroup[] }[] = [];
  for (const group of groups) {
    const last = sections[sections.length - 1];
    if (last && last.label === group.policyGroup) last.groups.push(group);
    else sections.push({ label: group.policyGroup, groups: [group] });
  }
  return sections;
}

/**
 * Fold a conditional field under its gating parent (same worksheet, `parentMapKey`) instead of
 * listing it as an unrelated sibling — e.g. "days past due before lien sale" folds under "has a
 * lien sale policy". Only folds when the parent is actually present in this same section; a child
 * whose parent didn't change (and so isn't in `sectionGroups`) still renders as its own top-level row.
 * Exported for PolicyWalkThrough, which folds the exact same way so a step-by-step review never
 * gets a child as its own unlinked step.
 */
export function nestGroups(sectionGroups: PolicyReviewGroup[]): { group: PolicyReviewGroup; children: PolicyReviewGroup[] }[] {
  const byMapKey = new Map(sectionGroups.map((group) => [group.mapKey, group]));
  const childrenByParent = new Map<string, PolicyReviewGroup[]>();
  const topLevel: PolicyReviewGroup[] = [];
  for (const group of sectionGroups) {
    if (group.parentMapKey && byMapKey.has(group.parentMapKey)) {
      const siblings = childrenByParent.get(group.parentMapKey) ?? [];
      siblings.push(group);
      childrenByParent.set(group.parentMapKey, siblings);
    } else {
      topLevel.push(group);
    }
  }
  return topLevel.map((group) => ({ group, children: childrenByParent.get(group.mapKey) ?? [] }));
}

/**
 * "All updates" — one card per real catalog section (groups arrive from the server already
 * sorted by sortOrder/policyGroup/field index, so a section is just a contiguous run of the same
 * `policyGroup` label), each a bordered card of update rows. A field gated on another (`showsWhen`)
 * folds directly under its parent instead of repeating as an unrelated row.
 */
export function PolicyUpdatesList({ groups, allLocationOptions, handlers }: PolicyUpdatesListProps): React.ReactElement {
  const sections = sectionizeGroups(groups);

  if (sections.length === 0) {
    return <div className="rounded-md border border-grey-200 bg-white px-5 py-8 text-center text-[13px] text-grey-500">Nothing to review.</div>;
  }

  return (
    <div className="flex flex-col gap-5">
      {sections.map((section) => (
        <div key={section.label} className="flex flex-col gap-2.5">
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] font-bold tracking-[0.06em] text-grey-700 uppercase">{section.label}</span>
            <span className="text-[12px] text-grey-500">
              {section.groups.length} update{section.groups.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="overflow-hidden rounded-[5px] border border-grey-200 bg-white shadow-panel">
            <div className={filterClasses(ROW_GRID, "border-b border-grey-200 bg-grey-50 px-5")}>
              <span />
              <span className={ROW_HEADER_LABEL}>Field</span>
              <span className={ROW_HEADER_LABEL}>Today</span>
              <span />
              <span className={ROW_HEADER_LABEL}>After apply</span>
              <span className={ROW_HEADER_LABEL}>Locations</span>
              <span />
            </div>
            {nestGroups(section.groups).map(({ group, children }) => {
              // A "describe further" child (free text — e.g. "Other" lock style asking what kind, or
              // an auction platform's name) reads as part of the SAME answer, not a separate
              // question — normally edited only in the per-location breakdown below the parent row
              // (AffectedLocationsDetail), never on the parent row itself, since a single group-wide
              // describe value doesn't make sense once locations can each type something different.
              // But a pure org-scope group has no locations to fan out over — no breakdown exists at
              // all — so hiding the child there left it with nowhere to be seen or edited. It gets its
              // own always-visible nested row instead in that case, same as any other gated child
              // (e.g. a number of days).
              const inlineChildren = children.filter((child) => child.fieldType === "text" && group.locations.length > 0);
              const nestedChildren = children.filter((child) => child.fieldType !== "text" || group.locations.length === 0);
              return (
                <div key={group.groupKey}>
                  <PolicyUpdateRow
                    group={group}
                    childMapKeys={children.map((child) => child.mapKey)}
                    inlineChildren={inlineChildren}
                    allLocationOptions={allLocationOptions}
                    busy={handlers.busyGroupKeys.has(group.groupKey)}
                    handlers={handlers}
                  />
                  {nestedChildren.map((child) => (
                    <PolicyUpdateRow
                      key={child.groupKey}
                      group={child}
                      nested
                      allLocationOptions={allLocationOptions}
                      busy={handlers.busyGroupKeys.has(child.groupKey)}
                      handlers={handlers}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

interface PolicyUpdateRowProps {
  group: PolicyReviewGroup;
  /** True for a field folded under its gating parent (see nestGroups) — indents the row; also means
   * this row never gets its own Approve button (approved together with its parent instead). */
  nested?: boolean;
  /** Top-level rows only — the mapKeys of any gated children folded under this one, approved in the
   * same call as the parent. */
  childMapKeys?: string[];
  /** Top-level rows only — "describe further" (text-type) gated children, folded into the
   * approve call alongside the parent (see childMapKeys) but only ever displayed/edited in the
   * per-location breakdown (AffectedLocationsDetail) once this row is expanded — never on this
   * row itself. */
  inlineChildren?: PolicyReviewGroup[];
  allLocationOptions: SelectItem[];
  busy: boolean;
  handlers: PolicyReviewHandlers;
}

// `divide-x` gives every column a real vertical rule instead of relying on `gap` alone — a long
// "Today"/"After apply" value used to read as if it bled into its neighbor with nothing marking
// where one column ends and the next begins.
const ROW_GRID =
  "grid grid-cols-[80px_minmax(160px,1.1fr)_minmax(0,1fr)_18px_minmax(0,1.2fr)_150px_92px] items-center gap-3 divide-x divide-grey-100 py-3.5";
const ROW_HEADER_LABEL = "px-2 text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase";

function PolicyUpdateRow({
  group,
  nested = false,
  childMapKeys = [],
  inlineChildren = [],
  allLocationOptions,
  busy,
  handlers
}: PolicyUpdateRowProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  // Keyed off `locations.length`, not `scope`, so a caller can populate an org-scope group's
  // `locations` with known per-location overrides (e.g. the Cortex migrator, whose org-baseline
  // rows also carry override info) and get the same chip/expand affordance — a no-op for this
  // feature's own groups, which never populate `locations` on an org-scope group.
  const canExpand = group.locations.length > 0;
  const isApplied = group.scope === "org" ? !!group.orgApplied : group.applied;
  const isSelectType = group.fieldType === "single-select" || group.fieldType === "multi-select";
  // A locations-scope single-/multi-select's real widget (PolicyRadio, a checkbox list) doesn't fit
  // this dense grid row — it's read-only here, edited per-location in the "After apply" breakdown
  // instead. Org scope has no breakdown to fall back to (no location fan-out), so it stays editable
  // right here, via the same compact click-opens-a-picker control the breakdown uses.
  const editableHere = !isSelectType || group.scope === "org";
  const sourceText = todaySourceText(group);

  return (
    <div className="border-b border-grey-200 last:border-b-0">
      <div
        onClick={canExpand ? () => setExpanded((value) => !value) : undefined}
        className={filterClasses(
          ROW_GRID,
          // Same outer padding whether nested or not — a nested row's indent lives INSIDE the label
          // cell only (below), so every other column (today/arrow/after-apply/locations/skip) stays
          // pixel-aligned with its parent's same column. A left accent border is the nesting cue here.
          nested ? "border-l-2 border-grey-200 px-5 py-2.5" : "px-5",
          "hover:bg-grey-50",
          isApplied && "bg-grey-50",
          canExpand && "cursor-pointer"
        )}
      >
        {nested ? (
          <span />
        ) : isApplied ? (
          <span className="text-[12px] font-semibold text-teal-700">Approved</span>
        ) : (
          <button
            type="button"
            disabled={busy || handlers.approving}
            onClick={(event) => {
              event.stopPropagation();
              void handlers.onApprove(group, childMapKeys);
            }}
            className={filterClasses(
              "rounded-[4px] border border-orange-200 bg-orange-50 px-2.5 py-1 text-[12px] font-semibold text-orange-700 transition-colors hover:bg-orange-100",
              (busy || handlers.approving) && "cursor-not-allowed opacity-50"
            )}
          >
            Approve
          </button>
        )}

        <div className={filterClasses("flex min-w-0 flex-col", nested && "pl-4")}>
          <span className="flex min-w-0 items-center gap-1">
            {nested && <FiCornerDownRight className="size-3 shrink-0 text-grey-400" />}
            <span title={group.label} className="truncate text-sm font-semibold text-grey-900">
              {group.label}
            </span>
          </span>
          {group.policyTitle && (
            <span title={group.policyTitle} className="truncate text-[12px] text-grey-500">
              {group.policyTitle}
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-col items-start gap-1">
          <DefaultOrCustomizedBadge isDefault={isCurrentValueDefault(group)} />
          {sourceText ? (
            <span
              className="rounded-[4px] border border-grey-200 bg-grey-50 px-2 py-1 text-[12px] leading-[16px] [overflow-wrap:anywhere] text-grey-500 italic"
              title="The legacy text this proposed value was extracted from"
            >
              “{sourceText}”
            </span>
          ) : (
            <span className="text-[13px] leading-[17px] text-grey-500">{todaySummary(group)}</span>
          )}
        </div>
        <span className="text-center text-[13px] text-orange-500">→</span>

        <div className="flex min-w-0 items-baseline gap-2">
          {group.proposedValue === null ? (
            <span className="truncate text-[13px] text-grey-500 italic">Varies by location</span>
          ) : group.fieldType === "toggle" ? (
            <PolicyValueControl
              fieldType={group.fieldType}
              value={group.proposedValue}
              busy={busy}
              onChange={(value) => handlers.onEditValue(group, value)}
            />
          ) : !editableHere ? (
            <span className="truncate text-[14px] font-semibold text-grey-900" title="Edit per-location below">
              {displayPolicyValue(group.proposedValue, group.fieldType, group.options)}
            </span>
          ) : isSelectType ? (
            <CompactPolicyValueControl
              fieldType={group.fieldType}
              options={group.options}
              value={group.proposedValue}
              busy={busy}
              onChange={(value) => handlers.onEditValue(group, value)}
            />
          ) : (
            <FreeValueInput
              fieldType={group.fieldType}
              options={group.options}
              value={group.proposedValue}
              busy={busy}
              onChange={(value) => handlers.onEditValue(group, value)}
            />
          )}
        </div>

        {group.locations.length === 0 ? (
          <span className="justify-self-start rounded-full border border-grey-200 bg-grey-100 px-2.5 py-1 text-[12px] font-bold whitespace-nowrap text-grey-600">
            All locations
          </span>
        ) : (
          <div className="flex min-w-0 items-center gap-1 justify-self-start">
            {/* stopPropagation scoped tightly to the chip itself (not the whole cell) — its own
             * click-to-open wiring lives on a wrapping div Radix attaches internally, one DOM level
             * above the button we'd otherwise target, so it has to be caught here. */}
            <div onClick={(event) => event.stopPropagation()}>
              <AffectedLocationsField
                group={group}
                allLocationOptions={allLocationOptions}
                busy={busy}
                onToggleExcluded={(locationIds, excluded) => handlers.onToggleExcluded(group, locationIds, excluded)}
                onAddLocations={(locationIds) => handlers.onAddLocations(group, locationIds)}
              />
            </div>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((value) => !value);
              }}
              aria-label={expanded ? "Hide what changed per location" : "Show what changed per location"}
              title={expanded ? "Hide what changed per location" : "Show what changed per location"}
              className="shrink-0 rounded-full p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-grey-900"
            >
              <FiChevronDown className={filterClasses("size-3.5 transition-transform", expanded && "rotate-180")} />
            </button>
          </div>
        )}

        {group.scope === "org" ? (
          <button
            type="button"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              handlers.onToggleExcluded(group, null, !group.orgExcluded);
            }}
            className={filterClasses(
              "justify-self-end text-[13px] font-semibold transition-colors",
              group.orgExcluded ? "text-orange-500 hover:text-orange-600" : "text-grey-500 hover:text-grey-900",
              busy && "cursor-not-allowed opacity-60"
            )}
          >
            {group.orgExcluded ? "Include" : "Skip"}
          </button>
        ) : (
          <span />
        )}
      </div>

      {expanded && group.locations.length > 0 && (
        <div className="border-t border-grey-200 bg-grey-50 px-5 py-3">
          <AffectedLocationsDetail
            group={group}
            childGroups={inlineChildren}
            busy={busy}
            onEditValue={(value, locationId, targetGroup) => handlers.onEditValue(targetGroup ?? group, value, locationId)}
          />
        </div>
      )}
    </div>
  );
}

// "1"/"0" reads as Yes/No for a toggle field. A single-/multi-select field's stored optionKey(s)
// resolve to their real catalog label(s) — a yes/no QUESTION is often authored as a two-option
// select (optionKey "1"/"0", label "Yes"/"No"), not a boolean_checkbox, so the toggle case alone
// doesn't catch it; the options lookup does, for any select field, not just accidentally-binary ones.
export function displayPolicyValue(value: string, fieldType?: PolicyFieldType, options?: PolicyFieldOption[]): string {
  if (fieldType === "toggle") return value === "1" ? "Yes" : value === "0" ? "No" : value;
  if (options && options.length > 0) {
    const labelByKey = new Map(options.map((option) => [option.optionKey, option.label]));
    if (fieldType === "multi-select") {
      const tokens = value.split(",").filter((token) => token !== "");
      return tokens.length > 0 ? tokens.map((token) => labelByKey.get(token) ?? token).join(", ") : value;
    }
    return labelByKey.get(value) ?? value;
  }
  return value;
}

// "Varies by location" when the group's locations don't all propose the same value (see
// PolicyReviewGroup.proposedValue's doc comment) — the group-level counterpart to todaySummary
// below, which already does this for the CURRENT-value side. Org scope always has exactly one
// candidate (no fan-out to disagree across), so proposedValue there is never null.
export function proposedSummary(group: PolicyReviewGroup): string {
  return group.proposedValue === null ? "Varies by location" : displayPolicyValue(group.proposedValue, group.fieldType, group.options);
}

// "Varies by location" when active (non-excluded) locations disagree on today's value; otherwise
// the one value they share. Org scope has a single current answer, no aggregation needed.
export function todaySummary(group: PolicyReviewGroup): string {
  if (group.scope === "org") {
    if (group.orgCurrentValueIsDefault) return "Not previously set";
    const value = group.orgCurrentValue;
    return value === null || value === undefined || value === "" ? "Not set" : displayPolicyValue(value, group.fieldType, group.options);
  }
  const active = group.locations.filter((location) => !location.excluded);
  if (active.length === 0) return "—";
  if (active.every((location) => location.currentValueIsDefault)) return "Not previously set";
  const values = active.map((location) => location.currentValue ?? "");
  const distinct = new Set(values);
  if (distinct.size > 1) return "Varies by location";
  return values[0] === "" ? "Not set" : displayPolicyValue(values[0], group.fieldType, group.options);
}

// True only when EVERY active (non-excluded) location's current answer is catalog-default-sourced —
// org scope has a single current answer, no aggregation needed. Mirrors todaySummary's "Not
// previously set" condition; kept as its own export so a caller can render "Default"/"Customized"
// explicitly instead of (or alongside) todaySummary's prose.
export function isCurrentValueDefault(group: PolicyReviewGroup): boolean {
  return group.scope === "org"
    ? group.orgCurrentValueIsDefault === true
    : group.locations.length > 0 &&
        group.locations.filter((location) => !location.excluded).every((location) => location.currentValueIsDefault === true);
}

// The legacy free text that justifies the proposed value, shown in the "Today" slot in place of "Not
// previously set" — when nothing was ever really answered, that slot would otherwise just say "we
// don't know," wasting the one place on the row that could instead answer "where did this value come
// from." Only returned when `todaySummary` would say "Not previously set" AND there's actually a
// citation/rawExtracted to show; null otherwise (todaySummary's normal text applies then).
export function todaySourceText(group: PolicyReviewGroup): string | null {
  if (!isCurrentValueDefault(group)) return null;
  return (group.citation && group.citation.trim()) || (group.rawExtracted && group.rawExtracted.trim()) || null;
}

/** "Default" (never really answered) vs "Customized" (a real prior answer existed) — an explicit
 * badge alongside todaySummary/todaySourceText's prose, so the distinction isn't only implied by
 * which of those two rendered. */
export function DefaultOrCustomizedBadge({ isDefault }: { isDefault: boolean }): React.ReactElement {
  return (
    <span
      className={filterClasses(
        "inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-bold tracking-[0.04em] whitespace-nowrap uppercase",
        isDefault ? "bg-grey-100 text-grey-500" : "bg-teal-50 text-teal-700"
      )}
    >
      {isDefault ? "Default" : "Customized"}
    </span>
  );
}
